/**
 * `?demo=<mode>-rookie` and `?demo=<mode>-pro`: a field game's demo on a
 * Rookie or Pro turn (app kidDistances.ts, Cole 2026-10-29): the same fixture
 * with the putter's field shrunk to that level's ladder, through the app's
 * own view helpers, so the TV shows what the phone sends on that turn.
 * Football, Baseball, Line Four, Turf War and Darts (`football-rookie`,
 * `footballfield-pro`, `baseball-rookie`, `linefour-pro`, `turfwar-rookie`,
 * `cricket-pro`, ...). The old `-kid` suffix still opens Rookie.
 */
import type { TvPuttEvent, TvScene, TvSnapshot } from './protocol';
import {
  ADULT_DISTANCES,
  ballForView,
  bandsForView,
  cupsForView,
  levelDistanceView,
  seatLevelLabel,
  type DistanceView,
  type LevelMaps,
  type ShortLevel,
} from '../../../app/LagKing/src/multiplayerGames/kidDistances';
import {
  BASEBALL_LEVEL_MAPS,
  CRICKET_LEVEL_MAPS,
  FOOTBALL_LEVEL_FIELDS,
  FOOTBALL_LEVEL_MAPS,
  LINE_FOUR_LEVEL_MAPS,
  TURF_WAR_LEVEL_MAPS,
} from '../../../app/LagKing/src/multiplayerGames/kidModes';
import { lineFourRolloutBands } from '../../../app/LagKing/src/multiplayerGames/lineFourBands';
import { turfWarRolloutBands, turfWarZoneLabel } from '../../../app/LagKing/src/multiplayerGames/turfWarBands';
import { cricketDartBoard, cricketRolloutBands, cricketTableRows } from '../../../app/LagKing/src/multiplayerGames/cricketView';
import { createCricketGame } from '../../../app/LagKing/src/multiplayerGames/cricket';
import { TURF_WAR_ZONES } from '../../../app/LagKing/src/multiplayerGames/turfWar';
import type { LineFourBoard } from '../../../app/LagKing/src/multiplayerGames/lineFour';
import type { TurfWarState } from '../../../app/LagKing/src/multiplayerGames/turfWar';

/** Each demo suffix and the level it shows (`-kid`: the old name for Rookie). */
export const LEVEL_DEMO_SUFFIXES: ReadonlyArray<readonly [string, ShortLevel]> = [
  ['-rookie', 'rookie'],
  ['-pro', 'pro'],
  ['-kid', 'rookie'],
];

const MAPS: Record<string, LevelMaps> = {
  football: FOOTBALL_LEVEL_MAPS,
  baseball: BASEBALL_LEVEL_MAPS,
  lineFour: LINE_FOUR_LEVEL_MAPS,
  turfWar: TURF_WAR_LEVEL_MAPS,
  cricket: CRICKET_LEVEL_MAPS,
};

/** The base mode and level of a level demo, or null when `rawMode` is not one. */
export function levelDemoMode(rawMode: string | null): { base: string; level: ShortLevel } | null {
  const mode = (rawMode ?? '').toLowerCase();
  for (const [suffix, level] of LEVEL_DEMO_SUFFIXES) {
    if (mode.endsWith(suffix)) return { base: mode.slice(0, -suffix.length), level };
  }
  return null;
}

/** The game kind a fixture shows: its game state's, or Football for the field-only demos. */
function kindOf(snapshot: TvSnapshot, scene: Extract<TvScene, { kind: 'putt' }>): string | null {
  const kind = (snapshot.game as { kind?: unknown } | null | undefined)?.kind;
  if (typeof kind === 'string' && kind in MAPS) return kind;
  return scene.footballField ? 'football' : null;
}

export function levelDemoFixture(fixture: { snapshot: TvSnapshot; event: TvPuttEvent }, level: ShortLevel): { snapshot: TvSnapshot; event: TvPuttEvent } {
  const scene = fixture.snapshot.scene as Extract<TvScene, { kind: 'putt' }> | undefined;
  if (!scene || scene.kind !== 'putt') return fixture;
  const kind = kindOf(fixture.snapshot, scene);
  if (!kind) return fixture;
  const view: DistanceView = levelDistanceView(MAPS[kind][level]);
  const game = fixture.snapshot.game as Record<string, unknown> | null | undefined;
  const nextScene: Extract<TvScene, { kind: 'putt' }> = {
    ...scene,
    targetFt: view.ft(scene.targetFt),
    extraCups: scene.extraCups ? cupsForView(scene.extraCups, view) : scene.extraCups,
    // A ball left on the green (the offense's, the batter's) was an adult's.
    extraBalls: (scene.extraBalls ?? []).map((ball) => ({ ...ball, ...ballForView(-ball.zFt, Math.atan2(ball.xFt, -ball.zFt) * (180 / Math.PI), ADULT_DISTANCES, view) })),
  };
  if (kind === 'football' && scene.footballField) nextScene.footballField = { ...scene.footballField, ...FOOTBALL_LEVEL_FIELDS[level] };
  if (kind === 'football' || kind === 'baseball') nextScene.rolloutBands = bandsForView(scene.rolloutBands ?? [], view);
  if (kind === 'lineFour') {
    const board = (game?.state as { board?: LineFourBoard } | undefined)?.board ?? null;
    nextScene.rolloutBands = lineFourRolloutBands(board, view);
  }
  if (kind === 'turfWar' && game?.state) {
    nextScene.rolloutBands = turfWarRolloutBands(game.state as TurfWarState, { players: ['#f0605d', '#4fc3f7'], open: '#dba94d' }, view);
  }
  if (kind === 'cricket') {
    nextScene.rolloutBands = cricketRolloutBands(null, view);
    nextScene.dartBoard = cricketDartBoard(null, view);
  }
  // The putter plays the level: the scoreboards tag the name.
  const names = Array.isArray(game?.names) ? (game!.names as string[]) : null;
  const putter = typeof game?.putterTeam === 'number' ? game.putterTeam : typeof game?.activePlayerIndex === 'number' ? game.activePlayerIndex : typeof game?.turn === 'number' ? game.turn : 0;
  const nextGame = game && names
    ? {
        ...game,
        names: names.map((name, index) => seatLevelLabel(name, index === putter ? level : 'tour')),
        // The big corner distance (Football's defense, Baseball's fielder) is the level's cup.
        ...(typeof game.targetFt === 'number' ? { targetFt: view.targetFt(game.targetFt) } : {}),
        // What the phone sends on a Rookie or Pro turn: Baseball's legend, Turf War's zone table, Darts' table.
        ...(Array.isArray(game.holesFt) ? { holesFt: (game.holesFt as number[]).map((ft) => view.targetFt(ft)) } : {}),
        ...(typeof game.buntFt === 'number' ? { buntFt: view.targetFt(game.buntFt) } : {}),
        ...(kind === 'turfWar' ? { zoneLabels: TURF_WAR_ZONES.map((zone) => turfWarZoneLabel(zone, view)) } : {}),
        ...(kind === 'cricket' && Array.isArray(game.table)
          ? { table: (game.table as Array<Record<string, unknown>>).map((row, index) => ({ ...row, distance: cricketTableRows(createCricketGame({ players: 2 }), view)[index]?.distance ?? row.distance })) }
          : {}),
      }
    : game;
  return {
    snapshot: { ...fixture.snapshot, scene: nextScene, ...(nextGame ? { game: nextGame as TvSnapshot['game'] } : {}), targetFt: view.ft(fixture.snapshot.targetFt ?? scene.targetFt) },
    event: {
      ...fixture.event,
      distanceFt: view.ft(fixture.event.distanceFt),
      targetFt: fixture.event.targetFt === null || fixture.event.targetFt === undefined ? fixture.event.targetFt : view.targetFt(fixture.event.targetFt),
      proximityFt: fixture.event.proximityFt === null || fixture.event.proximityFt === undefined ? fixture.event.proximityFt : view.tolFt(fixture.event.proximityFt),
    },
  };
}
